import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotification } from '../../hooks/useNotification';
import ClassificationChip from '../common/ClassificationChip';
import SortableTable, { HeadCell } from '../common/SortableTable';
import { Criteria, ReviewedOutput, ReviewedOutputSource, User } from '../../types';
import {
  deleteReviewedOutput,
  fetchCriteria,
  fetchReviewedOutputs,
  fetchUsers,
} from '../../services/api';

const SOURCE_LABEL: Record<ReviewedOutputSource, string> = {
  output_review: 'Output review',
  disagreement_review: 'Disagreement',
};

const ReviewedOutputs: React.FC = () => {
  const [reviewedOutputs, setReviewedOutputs] = useState<ReviewedOutput[]>([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [criteria, setCriteria] = useState<Criteria[]>([]);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [outputToDelete, setOutputToDelete] = useState<ReviewedOutput | null>(null);

  const { notification, showNotification, hideNotification } = useNotification();

  useEffect(() => {
    fetchReviewedOutputs()
      .then(data => {
        setReviewedOutputs(data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        showNotification('Failed to load reviewed outputs', 'error');
      });
    fetchUsers()
      .then(setUsers)
      .catch(() => showNotification('Failed to load users', 'error'));
    fetchCriteria()
      .then(setCriteria)
      .catch(() => showNotification('Failed to load criteria', 'error'));
  }, [showNotification]);

  const usersById = useMemo(() => {
    const map: Record<string, User> = {};
    for (const u of users) {
      map[u.id] = u;
    }
    return map;
  }, [users]);

  const criteriaById = useMemo(() => {
    const map: Record<string, Criteria> = {};
    for (const c of criteria) {
      map[c.id] = c;
    }
    return map;
  }, [criteria]);

  const handleDeleteClick = (output: ReviewedOutput) => {
    setOutputToDelete(output);
    setShowDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
    setOutputToDelete(null);
  };

  const confirmDelete = async () => {
    if (!outputToDelete) return;
    try {
      await deleteReviewedOutput(outputToDelete.id);
      setReviewedOutputs(prev => prev.filter(r => r.id !== outputToDelete.id));
    } catch {
      showNotification('Failed to delete reviewed output', 'error');
    }
    setShowDeleteDialog(false);
    setOutputToDelete(null);
  };

  const getReviewerName = useCallback(
    (reviewedBy: string) => {
      const user = usersById[reviewedBy];
      return user ? `${user.firstName} ${user.lastName}` : reviewedBy;
    },
    [usersById]
  );

  const headCells: HeadCell<ReviewedOutput>[] = useMemo(
    () => [
      {
        id: 'sentence',
        label: 'Sentence',
        numeric: false,
        sortable: true,
        width: '26%',
      },
      {
        id: 'criteriaId',
        label: 'Criteria',
        numeric: false,
        sortable: true,
        width: '12%',
        renderCell: output => criteriaById[output.criteriaId]?.name ?? 'Unknown',
        getComparableValue: output => criteriaById[output.criteriaId]?.name ?? 'Unknown',
      },
      {
        id: 'source',
        label: 'Source',
        numeric: false,
        sortable: true,
        width: '10%',
        renderCell: output => <Chip size="small" label={SOURCE_LABEL[output.source]} />,
        getComparableValue: output => SOURCE_LABEL[output.source],
      },
      {
        id: 'reviewedBy',
        label: 'Reviewed By',
        numeric: false,
        sortable: true,
        width: '11%',
        renderCell: output => getReviewerName(output.reviewedBy),
        getComparableValue: output => getReviewerName(output.reviewedBy),
      },
      {
        id: 'originalClassification',
        label: 'AI label',
        numeric: false,
        sortable: true,
        width: '8%',
        renderCell: output => <ClassificationChip value={output.originalClassification} />,
        getComparableValue: output => output.originalClassification,
      },
      {
        id: 'correctedClassification',
        label: 'Reviewed label',
        numeric: false,
        sortable: true,
        width: '8%',
        renderCell: output => <ClassificationChip value={output.correctedClassification} />,
        getComparableValue: output => output.correctedClassification,
      },
      {
        id: 'reason',
        label: 'Reason',
        numeric: false,
        sortable: false,
        width: '15%',
      },
      {
        id: 'reviewedAt',
        label: 'Reviewed At',
        numeric: false,
        sortable: true,
        width: '10%',
        renderCell: output => new Date(output.reviewedAt).toLocaleString(),
        getComparableValue: output => new Date(output.reviewedAt).getTime(),
      },
      {
        id: 'actions',
        label: '',
        numeric: false,
        sortable: false,
        renderCell: output => (
          <Button size="small" color="secondary" onClick={() => handleDeleteClick(output)}>
            Delete
          </Button>
        ),
      },
    ],
    [criteriaById, getReviewerName]
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
        Reviewed Outputs
      </Typography>
      <SortableTable
        data={reviewedOutputs}
        headCells={headCells}
        defaultSortBy="reviewedAt"
        defaultOrder="desc"
        idAccessor={output => output.id}
        emptyMessage="No reviewed outputs yet"
      />

      {outputToDelete && (
        <Dialog open={showDeleteDialog} onClose={handleCloseDeleteDialog}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the reviewed output for sentence{' '}
              <strong>
                &quot;{outputToDelete.sentence.substring(0, 80)}
                {outputToDelete.sentence.length > 80 ? '...' : ''}&quot;
              </strong>{' '}
              reviewed by <strong>{getReviewerName(outputToDelete.reviewedBy)}</strong>?{' '}
              {outputToDelete.source === 'disagreement_review'
                ? 'It will return to the resolve queue.'
                : 'This action cannot be undone.'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog} color="primary">
              Cancel
            </Button>
            <Button onClick={confirmDelete} color="secondary" autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={hideNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={hideNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ReviewedOutputs;
